import { createActions, createReducer } from "reduxsauce"
import { chain, forEach, get, has, includes, isEmpty, keys, map, pick, reduce, some, split, unset } from "lodash"
import { buffers, eventChannel } from "redux-saga"
import { all, call, put, select, take, takeEvery, takeLatest } from "redux-saga/effects"
import io from "socket.io-client"
import moment from "moment"
import update, { extend } from "immutability-helper"
import { get as apiGet, post as apiPost } from "api"
import { sendQuery } from "api/graphql"
import { OK, ERROR, LOADING } from "helpers/data-statuses"
import { generateIntradayBatchUrlAt, getBatchMomentOrTimeOnly, getIntradayBatchUrlAsOf, resolveIntradayLastBatchResource } from "helpers/intraday"
import { Creators as GraphqlCreators } from "./graphql"
import { Creators as DataCreators } from "./data"

// Extension to immutability-helper to allow autovivification of object with depth
extend("$auto", (value, object) => object ? update(object, value) : update({}, value))

export const REFRESHING = "refreshing"
export const CANCELLED = "cancelled"

const BATCH_ANNOUNCED = "batchAnnounced"
const SOCKET_CONNECTED = "socketConnected"

export const { Types, Creators } = createActions({
  allQueriesExecuted: ["channel", "idx"],
  cleanupData: ["channel", "idx", "resource"],
  initIntradayListener: ["channel"],
  queryExecuted: ["channel", "idx", "resource", "data"],
  refreshData: ["channel"],
  refreshQuery: ["channel", "idx", "resource", "queryInfo"],
  registerQuery: ["module", "channel", "resource", "queryInfo"],
  setRefreshStatus: ["status", "channel", "refreshIdx", "currentDataIdx"],
  unregisterQuery: ["module", "channel", "resource"],
  unregisterModuleQueries: ["module"],
  cancelQuery: ["channel", "idx", "resource"],
  queueRefresh: ["channel"],
  clearQueuedRefresh: ["channel"],
}, {
  prefix: "ompnt/modules/intraday/",
})

const sockets = {}
export const INITIAL_STATE = {
  data: {},
  queries: {},
  pendingRefresh: {},
  refresh: {
    refreshIdx: null,
    currentDataIdx: null,
    status: OK,
    channel: null,
  },
}

const getQueriesStatus = (data, channel, idx) => chain(data)
  .get([channel, idx])
  .reduce((previous, { status: current }) => {
    // If at least one is refreshing, global status should be refreshing
    if (previous === LOADING || current === LOADING) {
      return LOADING
    }
    // If none is refreshing, check for errors
    if (previous === ERROR || current === ERROR) {
      return ERROR
    }
    return OK
  }, OK)
  .value()

const setQueryStatus = ({ state, channel, idx, resource, status }) => update(state, {
  data: {
    $auto: {
      [channel]: {
        $auto: {
          [idx]: {
            $auto: {
              [resource]: {
                $auto: {
                  status: {
                    $set: status,
                  },
                },
              },
            },
          },
        },
      },
    },
  },
})

const socketExists = (channel) => has(sockets, channel)

export const getSocket = (channel) => get(sockets, channel)

export const openSocket = (channel) => {
  const [account, resourceId] = split(channel, "_")

  sockets[channel] = io({
    query: {
      account,
      resourceId,
    },
    transports: ["websocket", "polling"],
  })
}

export const closeSocket = (channel) => {
  const socket = getSocket(channel)

  if (socket && socket.connected) {
    socket.disconnect()
  }
  unset(sockets, channel)
}

export const closeAllSockets = () => {
  const socketChannels = keys(sockets)
  forEach(socketChannels, channel => closeSocket(channel))
}

export const handleCleanupData = (state, { channel, idx, resource }) => {
  const { data: currentData } = state
  let data
  let shouldRemoveIdx = !resource
  if (resource && get(currentData, [channel, idx])) {
    data = update(currentData, { [channel]: { [idx]: { $unset: [resource] } } })
    shouldRemoveIdx = chain(data).get([channel, idx]).isEmpty().value()
  }
  if (shouldRemoveIdx && get(currentData, channel)) {
    data = update(currentData, { [channel]: { $unset: [idx] } })
  }
  return { ...state, data }
}

export const handleQueryExecuted = (state, { channel, idx, resource, data }) => {
  const { status } = get(state, ["data", channel, idx, resource], {})

  if (status !== CANCELLED) {
    return update(state, {
      data: {
        $auto: {
          [channel]: {
            $auto: {
              [idx]: {
                $auto: {
                  [resource]: {
                    $set: data,
                  },
                },
              },
            },
          },
        },
      },
    })
  }
  return state
}

export const handleRegisterQuery = (state, { module, channel, resource, queryInfo }) => update(state, {
  queries: {
    $auto: {
      [channel]: {
        $auto: {
          [resource]: {
            $apply: (resource) => {
              const modules = get(resource, "modules", [])
              if (!includes(modules, module)) {
                return { modules: update(modules, { $push: [module] }), ...queryInfo }
              }
              return { modules, ...queryInfo }
            },
          },
        },
      },
    },
  },
})

export const handleSetRefreshStatus = (state, refreshStatus) => ({ ...state, refresh: { ...pick(refreshStatus, ["status", "channel", "refreshIdx", "currentDataIdx"]) } })

export const handleUnregisterQuery = (state, { module, channel, resource }) => {
  const { queries: currentQueries, data: currentData, refresh: { currentDataIdx } } = state

  const modules = chain(currentQueries)
    .get([channel, resource, "modules"], [])
    .without(module)
    .value()

  let queries
  let cleanedData
  if (isEmpty(modules)) {
    // If currentQueries is empty there is nothing to unset so calling it will trigger an error
    if (!isEmpty(currentQueries)) {
      queries = update(currentQueries, {
        [channel]: {
          $unset: [resource],
        },
      })
    }
    if (currentDataIdx !== null) {
      cleanedData = handleCleanupData(state, { channel, idx: currentDataIdx, resource }).data
    }
  } else {
    queries = update(currentQueries, {
      [channel]: {
        [resource]: {
          modules: {
            $set: modules,
          },
        },
      },
    })
  }

  const noQueriesLeftOnChannel = chain(queries).get(channel).isEmpty().value()
  if (noQueriesLeftOnChannel) {
    unset(queries, channel)
    if (socketExists(channel)) {
      closeSocket(channel)
    }
  }

  return { ...state, queries, data: cleanedData || currentData }
}

export const handleRefreshQuery = (state, { channel, idx, resource }) => setQueryStatus({ state, channel, idx, resource, status: LOADING })

export const handleCancelQuery = (state, { channel, idx, resource }) => setQueryStatus({ state, channel, idx, resource, status: CANCELLED })

export const handleQueueRefresh = (state, { channel }) => update(state, { pendingRefresh: { $auto: { [channel]: { $set: true } } } })

export const handleClearQueuedRefresh = (state, { channel }) => update(state, { pendingRefresh: { $auto: { $unset: [channel] } } })

export const reducer = createReducer(INITIAL_STATE, {
  [Types.CLEANUP_DATA]: handleCleanupData,
  [Types.QUERY_EXECUTED]: handleQueryExecuted,
  [Types.REGISTER_QUERY]: handleRegisterQuery,
  [Types.SET_REFRESH_STATUS]: handleSetRefreshStatus,
  [Types.UNREGISTER_QUERY]: handleUnregisterQuery,
  [Types.REFRESH_QUERY]: handleRefreshQuery,
  [Types.CANCEL_QUERY]: handleCancelQuery,
  [Types.QUEUE_REFRESH]: handleQueueRefresh,
  [Types.CLEAR_QUEUED_REFRESH]: handleClearQueuedRefresh,
})

export const sagaSelectors = {
  getQueries: state => get(state, ["global", "intraday", "queries"]),
  getRefresh: state => get(state, "global.intraday.refresh"),
  getIntraday: state => get(state, "global.intraday"),
  getAccountAlias: state => get(state, "global.selected.account"),
  getResourceData: (state, resource) => get(state, ["global", "data", resource, "data"]),
}

export function* handleRegisterQuerySaga({ channel, resource, queryInfo }) {
  const [{ status, refreshIdx: idx }, queries] = yield all([
    select(sagaSelectors.getRefresh),
    select(sagaSelectors.getQueries),
  ])

  const currentQuery = get(queries, [channel, resource])

  // If the query is already registered and a refresh is in progress, cancel it as data will load automatically
  if (currentQuery && status === REFRESHING) {
    yield put(Creators.cancelQuery(channel, idx, resource))
  }

  const { id, query, variables, account, options, beta, isGraphQl, url, payload, noToken } = queryInfo
  if (isGraphQl) {
    yield put(GraphqlCreators.load(resource, id, query, variables, account, options, beta))
  } else {
    yield put(DataCreators.load(resource, id, url, payload, noToken))
  }

  if (!socketExists(channel)) {
    openSocket(channel)
    yield put(Creators.initIntradayListener(channel))
  }
}

export function* handleUnregisterQuerySaga({ resource }) {
  const { refresh: { status, channel, refreshIdx: idx }, queries } = yield select(sagaSelectors.getIntraday)

  if (status === REFRESHING) {
    yield put(Creators.cancelQuery(channel, idx, resource))
  }

  const resourceStillRegistered = chain(queries)
    .map(query => keys(query))
    .flatMap()
    .some(name => name === resource)
    .value()

  // If no resource is registered for this query, we remove the data to prevent selectors
  // from using stale data in `global.data`. Data would not refresh in the globa.data and would
  // give some weird results in the application.
  if (!resourceStillRegistered) {
    yield put(GraphqlCreators.loadSuccess(resource, null, null, null, null))
  }
}

export function* handleUnregisterModuleQueries({ module: moduleToClean }) {
  const queries = yield select(sagaSelectors.getQueries)

  const queriesToClean = reduce(queries, (acc, query, channel) => {
    const resources = reduce(query, (acc, { modules }, resource) => {
      if (some(modules, module => module === moduleToClean)) {
        acc.push(resource)
      }
      return acc
    }, [])

    map(resources, resource => acc.push({ channel, resource }))
    return acc
  }, [])

  for (const queryToClean in queriesToClean) {
    const { channel, resource } = queriesToClean[queryToClean]
    yield put(Creators.unregisterQuery(moduleToClean, channel, resource))
  }
}

export function* reconcileLastBatch(channel) {
  const [, resourceId] = split(channel, "_")

  const [accountAlias, queries] = yield all([
    select(sagaSelectors.getAccountAlias),
    select(sagaSelectors.getQueries),
  ])

  const channelQueries = get(queries, channel)
  const resource = resolveIntradayLastBatchResource(channelQueries, resourceId)

  if (!accountAlias || !resource) {
    return
  }

  // A fresh asOf, never the registered URL's — that one only re-confirms what is already shown.
  let latestBatch
  try {
    latestBatch = yield call(apiGet, generateIntradayBatchUrlAt(accountAlias, resourceId, moment().format()))
  } catch (error) {
    console.error(`Intraday reconcile could not read the last completed batch for channel ${channel} resource ${resource}`, error)
    return
  }

  const latestMoment = getBatchMomentOrTimeOnly(latestBatch)
  if (!latestMoment) {
    return
  }

  const displayedBatch = yield select(sagaSelectors.getResourceData, resource)
  const floor = getBatchMomentOrTimeOnly(displayedBatch) || getIntradayBatchUrlAsOf(get(channelQueries, [resource, "url"]))

  if (!floor || latestMoment.isAfter(floor)) {
    yield put(Creators.refreshData(channel))
  }
}

function* initIntradayListener({ channel }) {
  try {
    yield takeLatest(Types.REFRESH_DATA, handleRefreshData)

    const socket = getSocket(channel)
    if (socket) {
      const socketChannel = eventChannel(emitter => {
        const onBatchAnnounced = () => emitter(BATCH_ANNOUNCED)
        const onConnect = () => emitter(SOCKET_CONNECTED)
        socket.on("intraday", onBatchAnnounced)
        socket.on("connect", onConnect)
        return () => {
          socket.off("intraday", onBatchAnnounced)
          socket.off("connect", onConnect)
        }
      }, buffers.expanding())

      while (true) {
        const event = yield take(socketChannel)
        if (event === SOCKET_CONNECTED) {
          yield call(reconcileLastBatch, channel)
        } else {
          yield put(Creators.refreshData(channel))
        }
      }
    }
  } catch (err) {
    console.log(err)
  }
}

export function* handleRefreshData({ channel }) {
  const [{ currentDataIdx, status }, queries] = yield all([
    select(sagaSelectors.getRefresh),
    select(sagaSelectors.getQueries),
  ])
  const channelQueries = get(queries, channel, {})

  if (isEmpty(channelQueries)) {
    return
  }

  if (status === REFRESHING) {
    yield put(Creators.queueRefresh(channel))
    return
  }

  const refreshIdx = currentDataIdx !== null ? currentDataIdx + 1 : 0
  yield put(Creators.setRefreshStatus(REFRESHING, channel, refreshIdx, currentDataIdx))
  for (const resource in channelQueries) {
    yield put(Creators.refreshQuery(channel, refreshIdx, resource, channelQueries[resource]))
  }
}

export function* handleRefreshQuerySaga({ channel, resource, idx, queryInfo }) {
  const { query, variables, id, account, beta, isGraphQl, payload, url, noToken } = queryInfo
  try {
    let result
    if (isGraphQl) {
      result = yield call(sendQuery, query, variables, account, beta)
    } else {
      let data
      if (!payload) {
        data = yield call(apiGet, url, noToken)
      } else {
        data = yield call(apiPost, url, payload, noToken)
      }
      result = { data }
    }

    yield put(Creators.queryExecuted(channel, idx, resource, { id, status: OK, ...result }))
  } catch (error) {
    yield put(DataCreators.loadError(resource, id, error))
    yield put(Creators.queryExecuted(channel, idx, resource, { id, status: ERROR }))
  }

  const { data, refresh: { status, channel: refreshingChannel, refreshIdx } } = yield select(sagaSelectors.getIntraday)

  // `refresh` is global, so a late settle from a superseded cycle would otherwise clobber the live one.
  const isLiveCycle = status === REFRESHING && refreshingChannel === channel && refreshIdx === idx
  if (isLiveCycle && getQueriesStatus(data, channel, idx) !== LOADING) {
    yield put(Creators.allQueriesExecuted(channel, idx))
  }
}

export function* handleAllQueriesExecuted({ channel, idx }) {
  const { data, queries = {}, pendingRefresh, refresh: { currentDataIdx } } = yield select(sagaSelectors.getIntraday)

  const executionStatus = getQueriesStatus(data, channel, idx)

  const fetchedData = chain(data)
    .get([channel, idx])
    .reduce((allData, current, resource) => {
      if (current.status !== CANCELLED && current.status !== ERROR) {
        allData[resource] = current
      }
      return allData
    }, {})
    .value()
  if (!isEmpty(fetchedData)) {
    yield put(DataCreators.intradayLoadSuccess(fetchedData))
  }

  if (currentDataIdx !== null) {
    for (const resource in get(data, [channel, currentDataIdx])) {
      const keepDataHistory = get(queries, [channel, resource, "keepDataHistory"], false)
      if (!keepDataHistory) {
        yield put(Creators.cleanupData(channel, currentDataIdx, resource))
      }
    }
  }

  yield put(Creators.setRefreshStatus(executionStatus, null, null, idx))

  // Re-entered directly, not re-dispatched: REFRESH_DATA is watched by takeLatest, so a second
  // replay in the same flush would cancel the first channel's fan-out partway through its puts.
  for (const pendingChannel in pendingRefresh) {
    yield put(Creators.clearQueuedRefresh(pendingChannel))
    try {
      yield call(handleRefreshData, { channel: pendingChannel })
    } catch (error) {
      // ALL_QUERIES_EXECUTED is watched by takeEvery with no onError: an escaping throw takes down
      // every intraday watcher for the session, not just this channel.
      console.error(`Intraday could not replay the queued refresh for channel ${pendingChannel}`, error)
    }
  }
}

export function* saga() {
  yield takeEvery(Types.REGISTER_QUERY, handleRegisterQuerySaga)
  yield takeEvery(Types.UNREGISTER_QUERY, handleUnregisterQuerySaga)
  yield takeEvery(Types.UNREGISTER_MODULE_QUERIES, handleUnregisterModuleQueries)
  yield takeLatest(Types.INIT_INTRADAY_LISTENER, initIntradayListener)
  yield takeEvery(Types.REFRESH_QUERY, handleRefreshQuerySaga)
  yield takeEvery(Types.ALL_QUERIES_EXECUTED, handleAllQueriesExecuted)
}
